import { useEffect, useState, useCallback, useRef } from "react";
import { Globe, ChevronDown, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "fr", label: "French" },
  { code: "de", label: "German" },
  { code: "es", label: "Spanish" },
  { code: "pt", label: "Portuguese" },
  { code: "it", label: "Italian" },
  { code: "nl", label: "Dutch" },
  { code: "ar", label: "Arabic" },
  { code: "zh-CN", label: "Chinese" },
  { code: "ja", label: "Japanese" },
  { code: "ko", label: "Korean" },
  { code: "ru", label: "Russian" },
];

let googleTranslateInitialized = false;
let googleTranslateLoadPromise = null;
const LANG_CHANGE_EVENT = "googletranslate-lang-change";
const GOOGLE_TRANSLATE_SCRIPT_SRC =
  "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";

const getRootDomain = () => {
  const hostname = window.location.hostname;
  if (hostname === "localhost" || /^\d+\.\d+\.\d+\.\d+$/.test(hostname))
    return hostname;
  const parts = hostname.split(".");
  if (parts.length <= 2) return `.${hostname}`;
  return `.${parts.slice(-2).join(".")}`;
};

const ensureTranslateElement = () => {
  if (document.getElementById("google_translate_element")) return;
  const container = document.createElement("div");
  container.id = "google_translate_element";
  document.body.appendChild(container);
};

const injectTranslateStyles = () => {
  const styleId = "google-translate-custom-styles";
  if (document.getElementById(styleId)) return;

  const style = document.createElement("style");
  style.id = styleId;
  style.innerHTML = `
    .goog-te-banner-frame { display: none !important; }
    body { top: 0 !important; }
    #google_translate_element { position: absolute !important; left: -9999px !important; opacity: 0 !important; }
    .goog-tooltip { display: none !important; }
    .goog-text-highlight { background-color: transparent !important; border: none !important; box-shadow: none !important; }
    .skiptranslate { display: none !important; }
    .goog-te-gadget { display: none !important; }
  `;
  document.head.appendChild(style);
};

const initGoogleTranslate = () => {
  if (googleTranslateInitialized) return true;
  if (!window.google?.translate?.TranslateElement) return false;

  try {
    ensureTranslateElement();
    const layoutValue =
      window.google.translate.TranslateElement.InlineLayout?.SIMPLE;
    new window.google.translate.TranslateElement(
      {
        pageLanguage: "en",
        includedLanguages: LANGUAGES.map((l) => l.code).join(","),
        layout: layoutValue,
        autoDisplay: false,
      },
      "google_translate_element",
    );
    googleTranslateInitialized = true;
    return true;
  } catch (e) {
    console.warn("Google Translate init error:", e);
    return false;
  }
};

const loadGoogleTranslate = () => {
  if (googleTranslateInitialized) return Promise.resolve(true);
  if (googleTranslateLoadPromise) return googleTranslateLoadPromise;

  ensureTranslateElement();
  injectTranslateStyles();

  googleTranslateLoadPromise = new Promise((resolve) => {
    const finish = () => resolve(initGoogleTranslate());
    window.googleTranslateElementInit = finish;

    const scriptExists = document.querySelector(
      'script[src*="translate.google.com"]',
    );

    if (!scriptExists) {
      const addScript = document.createElement("script");
      addScript.src = GOOGLE_TRANSLATE_SCRIPT_SRC;
      addScript.async = true;
      addScript.onerror = () => resolve(false);
      document.body.appendChild(addScript);
      return;
    }

    if (finish()) return;

    const intervalId = window.setInterval(() => {
      if (!initGoogleTranslate()) return;
      window.clearInterval(intervalId);
      resolve(true);
    }, 100);

    window.setTimeout(() => {
      window.clearInterval(intervalId);
      resolve(false);
    }, 5000);
  }).then((ready) => {
    if (!ready) googleTranslateLoadPromise = null;
    return ready;
  });

  return googleTranslateLoadPromise;
};

const GoogleTranslate = ({ direction = "up" }) => {
  const [currentLang, setCurrentLang] = useState(() => {
    const match = document.cookie.match(/googtrans=\/en\/([a-zA-Z-]+)/);
    return match ? match[1] : "en";
  });
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (currentLang !== "en") void loadGoogleTranslate();
  }, [currentLang]);

  useEffect(() => {
    const handleLangSync = (e) => setCurrentLang(e.detail);
    window.addEventListener(LANG_CHANGE_EVENT, handleLangSync);
    return () => window.removeEventListener(LANG_CHANGE_EVENT, handleLangSync);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setIsOpen(false);
    };
    const handleEscape = (e) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  const openLanguageMenu = useCallback(() => {
    setIsOpen((prev) => {
      const next = !prev;
      if (next) void loadGoogleTranslate();
      return next;
    });
  }, []);

  const changeLanguage = useCallback(
    async (langCode) => {
      if (langCode === currentLang) return;
      const rootDomain = getRootDomain();

      if (langCode === "en") {
        document.cookie =
          "googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/";
        document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname}`;
        document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${rootDomain}`;
        setCurrentLang("en");
        window.dispatchEvent(
          new window.CustomEvent(LANG_CHANGE_EVENT, { detail: "en" }),
        );
        window.location.reload();
        return;
      }

      await loadGoogleTranslate();
      const select = document.querySelector(".goog-te-combo");
      if (select) {
        select.value = langCode;
        select.dispatchEvent(new Event("change", { bubbles: true }));
      }

      document.cookie = `googtrans=/en/${langCode}; path=/`;
      document.cookie = `googtrans=/en/${langCode}; path=/; domain=${window.location.hostname}`;
      document.cookie = `googtrans=/en/${langCode}; path=/; domain=${rootDomain}`;
      setCurrentLang(langCode);
      setIsOpen(false);
      window.dispatchEvent(
        new window.CustomEvent(LANG_CHANGE_EVENT, { detail: langCode }),
      );
      if (!select) window.location.reload();
    },
    [currentLang],
  );

  const currentLangLabel =
    LANGUAGES.find((l) => l.code === currentLang)?.label || "English";

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={openLanguageMenu}
        className="notranslate flex items-center gap-1.5 h-8 px-2.5 rounded-md text-[13px] font-medium text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <Globe size={14} />
        <span>{currentLangLabel}</span>
        <ChevronDown
          size={12}
          className={`transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{
              opacity: 0,
              y: direction === "up" ? 4 : -4,
              scale: 0.95,
            }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: direction === "up" ? 4 : -4, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className={`absolute right-0 w-48 z-[9999] ${direction === "up" ? "bottom-full mb-1.5" : "top-full mt-1.5"}`}
          >
            <div
              className="notranslate bg-white dark:bg-gray-900 border border-gray-200/60 dark:border-gray-700/50 rounded-lg overflow-hidden p-1"
              style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.12)" }}
            >
              <div className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider border-b border-gray-100 dark:border-gray-800 mb-1">
                Language
              </div>

              <div className="max-h-[280px] overflow-y-auto" role="listbox">
                {LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    role="option"
                    aria-selected={currentLang === lang.code}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-[13px] transition-colors text-left ${
                      currentLang === lang.code
                        ? "bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white font-medium"
                        : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-800"
                    }`}
                  >
                    <span>{lang.label}</span>
                    {currentLang === lang.code && (
                      <Check
                        size={13}
                        className="text-gray-500 dark:text-gray-400"
                      />
                    )}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default GoogleTranslate;
